import { useMemo, useRef, useCallback, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { cn } from '../lib/utils';
import { generateDayColumns } from '../utils/dateUtils';
import { format } from 'date-fns';

const ROW_HEIGHT = 40;
const DAY_CELL_WIDTH = 28;
const SIDEBAR_WIDTH = 200;
const MONTH_HEADER_HEIGHT = 24;
const DAY_HEADER_HEIGHT = 24;

const getInitials = function(name) {
  const parts = name.split(' ');
  let result = '';
  for (let i = 0; i < parts.length && i < 2; i++) {
    if (parts[i][0]) result += parts[i][0];
  }
  return result.toUpperCase();
};

export const HolidayCalendar = ({ resources, holidays, startDate, endDate, onUpdateHoliday, canEdit = false }) => {
  const sidebarScrollRef = useRef(null);
  const headerScrollRef = useRef(null);
  const gridScrollRef = useRef(null);
  const isSyncingTopRef = useRef(false);
  const isSyncingLeftRef = useRef(false);

  const syncScrollTop = useCallback(function(sourceRef, targetRef) {
    if (!sourceRef.current || !targetRef.current) return;
    if (isSyncingTopRef.current) return;
    isSyncingTopRef.current = true;
    targetRef.current.scrollTop = sourceRef.current.scrollTop;
    requestAnimationFrame(function() { isSyncingTopRef.current = false; });
  }, []);

  const syncScrollLeft = useCallback(function(sourceRef, targetRef) {
    if (!sourceRef.current || !targetRef.current) return;
    if (isSyncingLeftRef.current) return;
    isSyncingLeftRef.current = true;
    targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
    requestAnimationFrame(function() { isSyncingLeftRef.current = false; });
  }, []);

  const { months, totalDays } = useMemo(function() {
    if (!startDate || !endDate) return { months: [], totalDays: 0 };
    return generateDayColumns(startDate, endDate);
  }, [startDate, endDate]);

  const allDays = useMemo(function() {
    const days = [];
    for (let m = 0; m < months.length; m++) {
      for (let d = 0; d < months[m].days.length; d++) {
        days.push(months[m].days[d]);
      }
    }
    return days;
  }, [months]);

  const today = useMemo(function() {
    return format(new Date(), 'yyyy-MM-dd');
  }, []);

  const [editModal, setEditModal] = useState({ open: false, resourceId: null, resourceName: '', dateStr: '', currentHours: 0 });

  const handleCellClick = useCallback(function(resourceId, resourceName, dateStr, currentHours) {
    if (!canEdit) return;
    setEditModal({ open: true, resourceId, resourceName, dateStr, currentHours });
  }, [canEdit]);

  const handleModalSelect = useCallback(function(hours) {
    if (onUpdateHoliday) onUpdateHoliday(editModal.resourceId, editModal.dateStr, hours);
    setEditModal(function(m) { return { ...m, open: false }; });
  }, [onUpdateHoliday, editModal.resourceId, editModal.dateStr]);

  const totalWidth = totalDays * DAY_CELL_WIDTH;

  // Build month header cells
  const monthHeaderCells = [];
  for (let m = 0; m < months.length; m++) {
    const month = months[m];
    const width = month.days.length * DAY_CELL_WIDTH;
    monthHeaderCells.push(
      <div
        key={month.key}
        className="text-xs font-semibold text-center border-r border-border flex items-center justify-center flex-shrink-0 bg-muted/30"
        style={{ width: width, height: MONTH_HEADER_HEIGHT }}
      >
        {month.key}
      </div>
    );
  }

  // Build day header cells
  const dayHeaderCells = [];
  for (let i = 0; i < allDays.length; i++) {
    const day = allDays[i];
    const dateStr = format(day.date, 'yyyy-MM-dd');
    const isToday = dateStr === today;
    dayHeaderCells.push(
      <div
        key={i}
        className={cn(
          'text-[10px] text-center border-r border-border/30 flex items-center justify-center flex-shrink-0 font-medium',
          day.isWeekend && 'text-muted-foreground/50 bg-muted/20',
          isToday && 'text-primary font-bold bg-primary/10'
        )}
        style={{ width: DAY_CELL_WIDTH, height: DAY_HEADER_HEIGHT }}
      >
        {day.label}
      </div>
    );
  }

  // Build resource rows (sidebar)
  const resourceRows = [];
  for (let i = 0; i < resources.length; i++) {
    const resource = resources[i];
    resourceRows.push(
      <div
        key={resource.id}
        className="flex items-center gap-2 px-3 border-b border-border/50"
        style={{ height: ROW_HEIGHT }}
      >
        <Avatar className="h-6 w-6 flex-shrink-0">
          <AvatarImage src={resource.avatar} alt={resource.name} />
          <AvatarFallback className="text-[9px] bg-primary/10">
            {getInitials(resource.name)}
          </AvatarFallback>
        </Avatar>
        <span className="text-xs truncate font-medium">{resource.name}</span>
      </div>
    );
  }

  // Build grid rows
  const gridRows = [];
  for (let i = 0; i < resources.length; i++) {
    const resource = resources[i];
    const resourceHolidays = holidays[resource.id] || {};
    const cells = [];

    for (let d = 0; d < allDays.length; d++) {
      const day = allDays[d];
      const dateStr = format(day.date, 'yyyy-MM-dd');
      const isToday = dateStr === today;
      const holidayHours = resourceHolidays[dateStr] || 0;
      const isHoliday = holidayHours > 0;
      const isHalfDay = isHoliday && holidayHours < 8;

      cells.push(
        <div
          key={d}
          className={cn(
            'border-r border-border/20 flex-shrink-0',
            day.isWeekend && 'bg-muted/20',
            isHoliday && !day.isWeekend && 'bg-green-500/25',
            isHoliday && day.isWeekend && 'bg-green-500/15',
            isToday && !isHoliday && 'bg-primary/5',
            day.isFirstOfMonth && 'border-l border-l-border/60',
            canEdit && 'cursor-pointer hover:ring-1 hover:ring-inset hover:ring-primary/40'
          )}
          style={{ width: DAY_CELL_WIDTH, height: ROW_HEIGHT }}
          title={isHoliday ? 'Ferie: ' + holidayHours + 'h' : (canEdit ? 'Clicca per aggiungere ferie' : '')}
          onClick={canEdit ? function() { handleCellClick(resource.id, resource.name, dateStr, holidayHours); } : undefined}
        >
          {isHoliday && (
            <div className="w-full h-full flex items-end justify-center pb-1">
              {isHalfDay ? (
                <div className="w-3 h-1.5 rounded-t-full bg-green-500/70" />
              ) : (
                <div className="w-3 h-3 rounded-full bg-green-500/70" />
              )}
            </div>
          )}
        </div>
      );
    }

    gridRows.push(
      <div
        key={'row-' + resource.id}
        className="flex border-b border-border/30"
        style={{ height: ROW_HEIGHT, width: totalWidth }}
      >
        {cells}
      </div>
    );
  }

  // Sum total vacation hours per resource for summary
  const holidaySummary = useMemo(function() {
    const summary = {};
    for (let i = 0; i < resources.length; i++) {
      const resource = resources[i];
      const rh = holidays[resource.id] || {};
      let totalHours = 0;
      const dates = Object.keys(rh);
      for (let j = 0; j < dates.length; j++) {
        totalHours += rh[dates[j]];
      }
      summary[resource.id] = totalHours;
    }
    return summary;
  }, [resources, holidays]);

  return (
    <div className="flex flex-col h-full border border-border rounded-lg overflow-hidden bg-card">
      {/* Header */}
      <div className="flex border-b border-border bg-muted/30 flex-shrink-0">
        <div
          className="flex-shrink-0 border-r border-border px-3 flex items-end pb-1 font-semibold text-sm"
          style={{ width: SIDEBAR_WIDTH, height: MONTH_HEADER_HEIGHT + DAY_HEADER_HEIGHT }}
        >
          Risorse
        </div>
        <div
          ref={headerScrollRef}
          className="flex-1 overflow-x-auto scrollbar-hide"
          onScroll={function() { syncScrollLeft(headerScrollRef, gridScrollRef); }}
        >
          <div style={{ width: totalWidth }}>
            <div className="flex">{monthHeaderCells}</div>
            <div className="flex">{dayHeaderCells}</div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        <div
          ref={sidebarScrollRef}
          className="flex-shrink-0 border-r border-border overflow-y-auto scrollbar-hide"
          style={{ width: SIDEBAR_WIDTH }}
          onScroll={function() { syncScrollTop(sidebarScrollRef, gridScrollRef); }}
        >
          {resourceRows}
        </div>

        <div
          ref={gridScrollRef}
          className="flex-1 overflow-auto"
          onScroll={function() {
            syncScrollTop(gridScrollRef, sidebarScrollRef);
            syncScrollLeft(gridScrollRef, headerScrollRef);
          }}
        >
          <div style={{ width: totalWidth }}>
            {gridRows}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 px-4 py-2 border-t border-border bg-muted/20 text-xs flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500/25 border border-green-500/40 flex items-end justify-center pb-0.5">
            <div className="w-2 h-2 rounded-full bg-green-500/70" />
          </div>
          <span className="text-muted-foreground">Ferie giornata intera (8h)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500/25 border border-green-500/40 flex items-end justify-center pb-0.5">
            <div className="w-2 h-1 rounded-t-full bg-green-500/70" />
          </div>
          <span className="text-muted-foreground">Mezza giornata (4h)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-muted/40 border border-border" />
          <span className="text-muted-foreground">Weekend</span>
        </div>
        {resources.length > 0 && (
          <div className="ml-auto text-muted-foreground">
            Totale ferie caricate: {resources.reduce(function(acc, r) { return acc + (holidaySummary[r.id] || 0); }, 0)}h
          </div>
        )}
      </div>

      {editModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={function() { setEditModal(function(m) { return { ...m, open: false }; }); }}
        >
          <div
            className="bg-card border border-border rounded-lg shadow-xl p-4 min-w-[220px]"
            onClick={function(e) { e.stopPropagation(); }}
          >
            <div className="text-sm font-semibold mb-0.5 truncate">{editModal.resourceName}</div>
            <div className="text-xs text-muted-foreground mb-3">
              {editModal.dateStr.split('-').reverse().join('/')}
            </div>
            <div className="flex flex-col gap-1.5">
              {[
                { label: 'Nessuna', hours: 0 },
                { label: 'Mezza giornata (4h)', hours: 4 },
                { label: 'Giornata intera (8h)', hours: 8 },
              ].map(function(opt) {
                return (
                  <button
                    key={opt.hours}
                    className={cn(
                      'text-sm px-3 py-2 rounded text-left transition-colors',
                      editModal.currentHours === opt.hours
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/40 hover:bg-muted/80'
                    )}
                    onClick={function() { handleModalSelect(opt.hours); }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayCalendar;
